import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import DramaCard from '@/components/feature/DramaCard';
import FilterPanel from './components/FilterPanel';
import { dramas } from '@/mocks/dramas';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

type SortKey = 'latest' | 'hot' | 'premiere';

const PAGE_SIZE = 6;

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '最新更新' },
  { key: 'hot', label: '热度' },
  { key: 'premiere', label: '首播时间' },
];

export default function Category() {
  const { type } = useParams<{ type?: string }>();
  const activeType = type ? decodeURIComponent(type) : '';

  const [selectedTypes, setSelectedTypes] = useState<string[]>(activeType ? [activeType] : []);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [sort, setSort] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setSelectedTypes(activeType ? [activeType] : []);
  }, [activeType]);

  useEffect(() => {
    setPage(1);
  }, [selectedTypes, selectedRegions, selectedYears, sort]);

  const title = activeType ? `${activeType}电视剧` : '全部电视剧';
  useDocumentMeta({
    title: `${title}免费在线观看 - 香草在线观看免费播放电视剧`,
    description: `${title}免费在线观看，香草视频为你汇总最新的${title}资源，支持按地区、年份筛选与热度排序，高清在线观看更省心。`,
    keywords: `${title},${activeType || '电视剧'}免费观看,香草在线观看,在线播放${activeType || '电视剧'},高清电视剧`,
  });

  const filtered = useMemo(() => {
    const list = dramas.filter((drama) => {
      const typeMatch =
        selectedTypes.length === 0 || drama.types.some((t) => selectedTypes.includes(t));
      const regionMatch =
        selectedRegions.length === 0 || selectedRegions.includes(drama.region);
      const yearMatch = selectedYears.length === 0 || selectedYears.includes(drama.year);
      return typeMatch && regionMatch && yearMatch;
    });

    return list.sort((a, b) => {
      if (sort === 'hot') return b.hot - a.hot;
      if (sort === 'premiere') return new Date(b.premiereDate).getTime() - new Date(a.premiereDate).getTime();
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [selectedTypes, selectedRegions, selectedYears, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice(0, page * PAGE_SIZE);

  const toggle = <T,>(list: T[], value: T, setter: (v: T[]) => void) => {
    setter(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setSelectedRegions([]);
    setSelectedYears([]);
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1 pt-16 md:pt-20">
        <div className="w-full px-4 md:px-8 py-6 md:py-10">
          <nav className="text-xs md:text-sm text-foreground-500 flex items-center gap-1.5 flex-wrap" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category" className="hover:text-primary-700">电视剧分类</Link>
            {activeType && (
              <>
                <i className="ri-arrow-right-s-line" />
                <span className="text-foreground-800">{activeType}</span>
              </>
            )}
          </nav>

          <div className="mt-4">
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">{title}</h1>
            <p className="mt-2 text-sm text-foreground-600 max-w-3xl">
              {activeType
                ? `为你精选 ${activeType} 类型的高清电视剧，可继续按地区、年份等条件筛选，结果按你的偏好实时刷新。`
                : '浏览全部高清电视剧，支持按类型、地区、年份多条件筛选与排序，快速找到你想追的剧集。'}
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 pb-16 flex flex-col lg:flex-row gap-8">
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 rounded-lg bg-background-100 border border-background-200 px-5 py-2">
              <FilterPanel
                selectedTypes={selectedTypes}
                selectedRegions={selectedRegions}
                selectedYears={selectedYears}
                onToggleType={(v) => toggle(selectedTypes, v, setSelectedTypes)}
                onToggleRegion={(v) => toggle(selectedRegions, v, setSelectedRegions)}
                onToggleYear={(v) => toggle(selectedYears, v, setSelectedYears)}
                onClear={clearFilters}
              />
            </div>
          </aside>

          <section className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setFilterOpen(true)}
                  className="lg:hidden inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-800 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-filter-3-line" />
                  筛选
                </button>
                <span className="text-sm text-foreground-500">
                  共 <strong className="text-foreground-900">{filtered.length}</strong> 部
                </span>
              </div>
              <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSort(option.key)}
                    className={`px-3 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      sort === option.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {visible.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-20 flex flex-col items-center text-center px-6">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-inbox-archive-line text-3xl" />
                </span>
                <h3 className="font-heading font-semibold text-base text-foreground-900">暂无符合条件的电视剧</h3>
                <p className="mt-2 text-sm text-foreground-500 max-w-sm">试试调整或清除筛选条件，发现更多精彩剧集。</p>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line" />
                  清除筛选
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {visible.map((drama, index) => (
                    <DramaCard key={drama.id} drama={drama} index={index} />
                  ))}
                </div>

                <div className="mt-8 flex flex-col items-center gap-4">
                  {page < totalPages && (
                    <button
                      type="button"
                      onClick={() => setPage((p) => p + 1)}
                      className="inline-flex items-center gap-2 h-11 px-8 rounded-full bg-background-100 border border-background-300 hover:border-primary-300 text-sm font-medium text-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-down-line" />
                      加载更多
                    </button>
                  )}
                  <div className="flex items-center justify-center gap-1.5 flex-wrap">
                    <button
                      type="button"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => {
                          setPage(num);
                          window.scrollTo({ top: 220, behavior: 'smooth' });
                        }}
                        className={`min-w-9 h-9 px-2 flex items-center justify-center rounded-md border text-sm cursor-pointer transition-colors ${
                          num === page
                            ? 'bg-primary-500 border-primary-500 text-background-50'
                            : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300'
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={page === totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </main>
      <Footer />

      {filterOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setFilterOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 px-5 pb-6 animate-fade-up">
            <div className="sticky top-0 bg-background-50 pt-4 pb-2 flex items-center justify-between">
              <span className="font-heading font-semibold text-foreground-950">筛选条件</span>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterPanel
              selectedTypes={selectedTypes}
              selectedRegions={selectedRegions}
              selectedYears={selectedYears}
              onToggleType={(v) => toggle(selectedTypes, v, setSelectedTypes)}
              onToggleRegion={(v) => toggle(selectedRegions, v, setSelectedRegions)}
              onToggleYear={(v) => toggle(selectedYears, v, setSelectedYears)}
              onClear={clearFilters}
            />
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-5 w-full h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              查看结果（{filtered.length}）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}